<template>
  <div class="restaurante-pedidos-online">
    <div class="card card-custom pedidos-shell">
      <main class="pedidos-page-body">
        <div class="pedidos-commandbar">
          <nav class="pedidos-view-tabs" aria-label="Visões dos pedidos online">
            <button
              v-for="view in viewOptions"
              :key="view.key"
              type="button"
              :class="{ active: activeView === view.key }"
              :aria-current="activeView === view.key ? 'page' : null"
              @click="selectView(view.key)"
            >
              <i :class="view.icon" aria-hidden="true"></i>
              {{ view.label }}
              <span v-if="view.count" class="view-count">
                {{ view.count }}
              </span>
            </button>
          </nav>

          <section class="pedidos-toolbar" aria-label="Busca e filtros">
            <label class="pedidos-search" for="pedido-online-search">
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                id="pedido-online-search"
                v-model="draftFilters.busca"
                type="search"
                autocomplete="off"
                placeholder="Buscar código, cliente ou telefone"
                @input="scheduleSearch"
                @keyup.enter="applyFilters"
              />
            </label>

            <div class="pedidos-filter-wrapper">
              <button
                type="button"
                class="btn btn-light-primary font-weight-bolder filter-toggle"
                :aria-expanded="filterPanelOpen ? 'true' : 'false'"
                aria-controls="pedidos-filter-panel"
                @click="filterPanelOpen = !filterPanelOpen"
              >
                <i class="fas fa-sliders-h mr-2" aria-hidden="true"></i>
                Filtros
                <span v-if="appliedFilterCount" class="filter-count">
                  {{ appliedFilterCount }}
                </span>
              </button>

              <div
                v-if="filterPanelOpen"
                id="pedidos-filter-panel"
                class="pedidos-filter-panel"
              >
                <div class="filter-panel-heading">
                  <strong>Filtrar pedidos</strong>
                  <button
                    type="button"
                    aria-label="Fechar filtros"
                    @click="filterPanelOpen = false"
                  >
                    <i class="fas fa-times" aria-hidden="true"></i>
                  </button>
                </div>

                <label>
                  <span>Modalidade</span>
                  <select
                    v-model="draftFilters.tipo_entrega"
                    class="form-control"
                  >
                    <option :value="null">Entrega e retirada</option>
                    <option value="delivery">Entrega</option>
                    <option value="pickup">Retirada</option>
                  </select>
                </label>

                <label>
                  <span>Pagamento</span>
                  <select
                    v-model="draftFilters.pagamento_status"
                    class="form-control"
                  >
                    <option :value="null">Todos</option>
                    <option value="pendente">Pendente</option>
                    <option value="pago">Pago</option>
                    <option value="expirado">Expirado</option>
                    <option value="cancelado">Cancelado</option>
                    <option value="falhou">Falhou</option>
                  </select>
                </label>

                <template v-if="activeView === 'history'">
                  <label>
                    <span>Resultado</span>
                    <select v-model="draftFilters.status" class="form-control">
                      <option :value="null">Todos os encerramentos</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                      <option value="recusado">Recusado</option>
                      <option value="pagamento_expirado">
                        Pagamento expirado
                      </option>
                    </select>
                  </label>
                  <div class="history-period-fields">
                    <label>
                      <span>De</span>
                      <input
                        v-model="draftFilters.from"
                        type="date"
                        class="form-control"
                      />
                    </label>
                    <label>
                      <span>Até</span>
                      <input
                        v-model="draftFilters.to"
                        type="date"
                        class="form-control"
                      />
                    </label>
                  </div>
                </template>

                <div class="filter-panel-actions">
                  <button
                    type="button"
                    class="btn btn-light font-weight-bolder"
                    @click="clearFilters"
                  >
                    Limpar
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary font-weight-bolder"
                    @click="applyFilters"
                  >
                    Aplicar filtros
                  </button>
                </div>
              </div>
            </div>

            <button
              v-if="!deliveryOnlyProfile"
              type="button"
              class="btn font-weight-bolder pedidos-sound-button"
              :class="
                soundEnabled && soundReady
                  ? 'btn-light-success'
                  : 'btn-light-warning'
              "
              :aria-pressed="soundEnabled && soundReady ? 'true' : 'false'"
              :title="
                soundEnabled && soundReady
                  ? 'Silenciar aviso de novos pedidos'
                  : 'Clique para liberar e testar o aviso sonoro'
              "
              @click="toggleOrderSound"
            >
              <i
                :class="
                  soundEnabled && soundReady
                    ? 'fas fa-volume-up'
                    : 'fas fa-volume-mute'
                "
                aria-hidden="true"
              ></i>
              {{ soundEnabled && soundReady ? "Som ativo" : "Ativar som" }}
            </button>

            <button
              type="button"
              class="btn btn-light-dark pedidos-refresh-button"
              :disabled="loadingCurrentView || actionLoading"
              :title="lastRefreshLabel || 'Atualizar pedidos'"
              aria-label="Atualizar pedidos"
              @click="refreshCurrentView"
            >
              <i class="fas fa-sync-alt" aria-hidden="true"></i>
            </button>
            <span class="pedidos-auto-refresh" :title="lastRefreshLabel">
              Atualiza a cada 60 s
            </span>
          </section>
        </div>

        <div
          v-if="activeView === 'history'"
          class="history-presets"
          aria-label="Atalhos de período"
        >
          <span>Período:</span>
          <button
            v-for="preset in historyPresets"
            :key="preset.key"
            type="button"
            :class="{ active: historyPreset === preset.key }"
            @click="applyHistoryPreset(preset.key)"
          >
            {{ preset.label }}
          </button>
        </div>

        <div
          v-if="feedbackMessage"
          class="pedidos-feedback"
          :class="`is-${feedbackType}`"
          role="alert"
        >
          {{ feedbackMessage }}
        </div>
        <div class="sr-only" aria-live="polite" aria-atomic="true">
          {{ liveMessage }}
        </div>

        <template v-if="activeView !== 'history'">
          <div
            v-if="activeView === 'stale'"
            class="stale-explanation"
            role="note"
          >
            <i class="fas fa-history" aria-hidden="true"></i>
            <div>
              <strong>Pendências com mais de 24 horas</strong>
              <span>
                Nada é cancelado automaticamente. Resolva cada pedido na etapa
                operacional em que ele parou.
              </span>
            </div>
          </div>

          <nav class="kanban-stage-tabs" aria-label="Etapas do pedido">
            <button
              v-for="lane in laneDefinitions"
              :key="lane.key"
              type="button"
              :class="{ active: activeMobileLane === lane.key }"
              :aria-current="activeMobileLane === lane.key ? 'page' : null"
              @click="selectMobileLane(lane.key)"
            >
              {{ lane.label }}
              <span>{{ currentBoard.lanes[lane.key].total }}</span>
            </button>
          </nav>

          <div
            class="kanban-board"
            :class="{ 'is-dragging': Boolean(draggedOrder) }"
            :aria-busy="loadingBoard ? 'true' : 'false'"
          >
            <pedido-kanban-lane
              v-for="lane in laneDefinitions"
              :key="lane.key"
              :definition="lane"
              :lane="currentBoard.lanes[lane.key]"
              :server-time="currentBoard.server_time"
              :now="clockNow"
              :active="activeMobileLane === lane.key"
              :selected-id="selectedId"
              :loading="loadingBoard"
              :loading-more="loadingMoreLane === lane.key"
              :drag-disabled="actionLoading || detailLoading || drawerVisible"
              :dragging="Boolean(draggedOrder)"
              :drop-allowed="
                Boolean(draggedOrder && moveAction(draggedOrder, lane.key))
              "
              @drag-start="draggedOrder = $event"
              @drag-end="draggedOrder = null"
              @move="moveOrder"
              @select="openOrder"
              @load-more="loadMoreLane"
            />
          </div>
        </template>

        <section v-else class="history-view" aria-labelledby="history-title">
          <header class="history-heading">
            <div>
              <h2 id="history-title">Histórico de pedidos</h2>
              <p>Pedidos encerrados no período selecionado.</p>
            </div>
            <strong>{{ history.total }} pedidos</strong>
          </header>

          <div
            v-if="loadingHistory && !history.items.length"
            class="page-state"
          >
            <span class="spinner-border" aria-hidden="true"></span>
            Carregando histórico...
          </div>

          <div v-else-if="!history.items.length" class="page-state page-empty">
            <i class="far fa-folder-open" aria-hidden="true"></i>
            <strong>Nenhum pedido encontrado</strong>
            <span>Ajuste o período ou os filtros para ampliar a consulta.</span>
          </div>

          <div v-else class="history-grid">
            <pedido-kanban-card
              v-for="order in history.items"
              :key="pedidoKey(order)"
              :order="{ ...order, lane: 'finalizados' }"
              :server-time="history.server_time"
              :now="clockNow"
              :active="String(selectedId) === String(pedidoKey(order))"
              @select="openOrder"
            />
          </div>

          <button
            v-if="history.next_cursor"
            type="button"
            class="btn btn-light-primary font-weight-bolder history-load-more"
            :disabled="loadingHistory"
            @click="loadHistory(false)"
          >
            <span
              v-if="loadingHistory"
              class="spinner-border spinner-border-sm mr-2"
              aria-hidden="true"
            ></span>
            Carregar mais pedidos
          </button>
        </section>
      </main>
    </div>

    <pedido-online-drawer
      :visible="drawerVisible"
      :order="selected"
      :loading="detailLoading"
      :action-loading="actionLoading"
      :primary-action="primaryAction"
      :available-actions="availableActions"
      :action-blocked-message="financialPermissionMessage"
      :timeline="timeline.items"
      :timeline-loading="timeline.loading"
      :timeline-next-cursor="timeline.next_cursor"
      :couriers="couriers"
      :couriers-loading="couriersLoading"
      :server-time="drawerServerTime"
      :now="clockNow"
      @close="closeDrawer"
      @primary="runPrimaryAction"
      @secondary="runSecondaryAction"
      @print="imprimirPedidoCompleto"
      @load-timeline="loadTimeline"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { userAccessStore } from "@/userAccessStore";
import PedidoKanbanCard from "./PedidoKanbanCard.vue";
import PedidoKanbanLane from "./PedidoKanbanLane.vue";
import PedidoOnlineDrawer from "./PedidoOnlineDrawer.vue";
import { processarFilaImpressao } from "../../services/restaurantePrintQueue";
import {
  isMissingEndpointError,
  normalizePayment,
} from "../../services/onlineOrder";
import {
  BOARD_LANES,
  boardFromLegacyList,
  emptyBoard,
  isDeliveryAwaitingReturn,
  mergeLane,
  normalizeBoard,
  normalizeCard,
  normalizeHistory,
  orderId,
  TERMINAL_STATUSES,
  unwrapResponse,
  uuidLike,
} from "./orderBoard";

import {
  createOrderSound,
  readSoundPreference,
  saveSoundPreference,
} from "./orderSound";

const REFRESH_INTERVAL_MS = 60000;
const CLOCK_INTERVAL_MS = 30000;
const SEARCH_DEBOUNCE_MS = 300;

const PRIMARY_ACTIONS = {
  accept: { key: "accept", label: "Aceitar e enviar para produção" },
  production: { key: "production", label: "Enviar para produção" },
  open_production: { key: "open_production", label: "Abrir produção" },
  checkout_pickup: {
    key: "checkout_pickup",
    label: "Receber e concluir retirada",
  },
  complete_pickup: { key: "complete_pickup", label: "Concluir retirada" },
  assign_courier: { key: "assign_courier", label: "Atribuir entregador" },
  checkout_delivery_return: {
    key: "checkout_delivery_return",
    label: "Receber e concluir retorno",
  },
  complete_delivery_return: {
    key: "complete_delivery_return",
    label: "Concluir retorno",
  },
  finalize_delivery: { key: "finalize_delivery", label: "Finalizar pedido" },
};

const ACTION_ALIASES = {
  aceitar: "accept",
  accept_and_produce: "accept",
  aceitar_producao: "accept",
  checkout: "checkout_pickup",
  receive_pickup: "checkout_pickup",
  concluir_retirada: "complete_pickup",
  dispatch: "assign_courier",
  delivery: "assign_courier",
  manage_delivery: "assign_courier",
  open_dispatch: "assign_courier",
  atribuir_entregador: "assign_courier",
  assign_courier: "assign_courier",
  delivery_assign: "assign_courier",
  aceitar_entrega: "assign_courier",
  accept_delivery: "assign_courier",
  delivery_accept: "assign_courier",
  sair_para_entrega: "assign_courier",
  start_delivery: "assign_courier",
  delivery_start: "assign_courier",
  iniciar_rota: "assign_courier",
  entregar: "assign_courier",
  mark_delivered: "assign_courier",
  delivery_delivered: "assign_courier",
  marcar_entregue: "assign_courier",
  receber_entrega: "checkout_delivery_return",
  checkout_delivery: "checkout_delivery_return",
  checkout_return: "checkout_delivery_return",
  concluir_retorno_entrega: "complete_delivery_return",
  complete_delivery_return: "complete_delivery_return",
  delivery_return: "complete_delivery_return",
};

function isoDate(date) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function historyPeriod(days) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - Math.max(0, days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

export default {
  name: "app-restaurante-pedidos-online",
  components: {
    PedidoKanbanCard,
    PedidoKanbanLane,
    PedidoOnlineDrawer,
  },
  data() {
    const period = historyPeriod(7);
    const filters = {
      busca: "",
      tipo_entrega: null,
      pagamento_status: null,
      status: null,
      from: period.from,
      to: period.to,
    };
    return {
      activeView: "operation",
      activeMobileLane: "novos",
      boards: {
        active: { ...normalizeBoard({}), lanes: emptyBoard() },
        stale: { ...normalizeBoard({ scope: "stale" }), lanes: emptyBoard() },
      },
      history: {
        items: [],
        total: 0,
        next_cursor: null,
        server_time: null,
        timezone: "America/Sao_Paulo",
      },
      draftFilters: { ...filters },
      appliedFilters: { ...filters },
      historyPreset: "7d",
      filterPanelOpen: false,
      selected: null,
      drawerVisible: false,
      detailLoading: false,
      actionLoading: false,
      draggedOrder: null,
      loadingBoard: false,
      loadingHistory: false,
      loadingMoreLane: null,
      timeline: { items: [], next_cursor: null, loading: false },
      lastRefreshAt: null,
      feedbackMessage: "",
      feedbackType: "info",
      lastErrorToastMessage: "",
      lastErrorToastAt: 0,
      liveMessage: "",
      refreshInterval: null,
      clockInterval: null,
      searchTimer: null,
      clockNow: Date.now(),
      boardRequestSequence: 0,
      historyRequestSequence: 0,
      detailRequestSequence: 0,
      timelineRequestSequence: 0,
      triggerElement: null,
      legacyBoardEndpoint: false,
      couriers: [],
      couriersLoading: false,
      soundEnabled: readSoundPreference(),
      soundReady: false,
      newOrderCursor: null,
      monitoringOrders: false,
      pendingOrderSound: false,
      orderMonitorStopped: false,
    };
  },
  computed: {
    ...mapGetters(["itsblocked", "currentUser"]),
    isAdmin() {
      return Boolean(this.currentUser?.admin);
    },
    laneDefinitions() {
      return this.deliveryOnlyProfile
        ? BOARD_LANES.filter((lane) => lane.key === "entrega")
        : BOARD_LANES;
    },
    deliveryOnlyProfile() {
      const access = userAccessStore.access || {};
      const disabled = userAccessStore.disabled || {};
      const orders =
        access.restaurantePedidosOnline && !disabled.restaurantePedidosOnline;
      const delivery =
        access.restauranteEntregasOnline && !disabled.restauranteEntregasOnline;
      return Boolean(
        this.$route.query.compat_delivery === "1" || (delivery && !orders)
      );
    },
    currentScope() {
      return this.activeView === "stale" ? "stale" : "active";
    },
    currentBoard() {
      return this.boards[this.currentScope];
    },
    loadingCurrentView() {
      return this.activeView === "history"
        ? this.loadingHistory
        : this.loadingBoard;
    },
    selectedId() {
      return this.selected ? orderId(this.selected) : null;
    },
    drawerServerTime() {
      return this.activeView === "history"
        ? this.history.server_time
        : this.currentBoard.server_time;
    },
    viewOptions() {
      const staleTotal = Math.max(
        Number(this.boards.active.stale_total || 0),
        Number(this.boards.stale.stale_total || 0)
      );
      return [
        { key: "operation", label: "Operação", icon: "fas fa-columns" },
        {
          key: "stale",
          label: "Pendências antigas",
          icon: "fas fa-exclamation-triangle",
          count: staleTotal,
        },
        { key: "history", label: "Histórico", icon: "fas fa-history" },
      ];
    },
    historyPresets() {
      return [
        { key: "today", label: "Hoje" },
        { key: "7d", label: "7 dias" },
        { key: "30d", label: "30 dias" },
        { key: "custom", label: "Personalizado" },
      ];
    },
    appliedFilterCount() {
      let count = 0;
      if (this.appliedFilters.tipo_entrega) count += 1;
      if (this.appliedFilters.pagamento_status) count += 1;
      if (this.activeView === "history" && this.appliedFilters.status)
        count += 1;
      return count;
    },
    payment() {
      if (!this.selected) return {};
      return normalizePayment(
        this.selected.payment ||
          this.selected.pagamento ||
          (Array.isArray(this.selected.pagamentos)
            ? this.selected.pagamentos[0]
            : {})
      );
    },
    paymentPending() {
      return !this.paymentOperationallyConfirmed;
    },
    paymentOperationallyConfirmed() {
      const context = this.selected?.checkout_context || {};
      const operation =
        this.selected?.operacao || this.selected?.operational_link || {};
      return Boolean(
        this.selected?.operational_payment_confirmed === true ||
          context.checkout_concluido === true ||
          this.payment.operationally_confirmed === true ||
          (operation.liquidado_at &&
            (operation.fechamento_id ||
              operation.nfe_id ||
              operation.pre_venda_id))
      );
    },
    checkoutContext() {
      const context = this.selected?.checkout_context;
      if (!context || !context.atendimento_id) return null;
      const enabled =
        context.habilitado === true ||
        context.enabled === true ||
        this.selected?.capabilities?.checkout === true;
      return enabled ? context : null;
    },
    canOpenCheckoutSelected() {
      return Boolean(
        this.checkoutContext &&
          this.hasCashierPermission &&
          (this.selected?.tipo_entrega !== "delivery" ||
            this.hasDeliveryPermission)
      );
    },
    hasCashierPermission() {
      if (this.isAdmin) return true;
      return Boolean(
        userAccessStore.access?.restauranteCaixa &&
          !userAccessStore.disabled?.restauranteCaixa
      );
    },
    hasDeliveryPermission() {
      const access = userAccessStore.access || {};
      const disabled = userAccessStore.disabled || {};
      const allowed =
        (access.restauranteEntregasOnline &&
          !disabled.restauranteEntregasOnline) ||
        this.selected?.capabilities?.dispatch === true;
      return Boolean(allowed);
    },
    checkoutActionOfferedByServer() {
      if (!this.selected) return false;
      const primary = this.selected.primary_action;
      const primaryKey = this.normalizeActionKey(
        typeof primary === "object"
          ? primary.key || primary.code || primary.action
          : primary
      );
      const actions = Array.isArray(this.selected.available_actions)
        ? this.selected.available_actions
        : [];
      return (
        ["checkout_pickup", "checkout_delivery_return"].includes(primaryKey) ||
        actions.some((action) =>
          ["checkout_pickup", "checkout_delivery_return"].includes(
            this.normalizeActionKey(action)
          )
        )
      );
    },
    deliveryAwaitingReturn() {
      return isDeliveryAwaitingReturn(this.selected);
    },
    financialPermissionMessage() {
      const checkoutContext = this.selected?.checkout_context || {};
      if (
        checkoutContext.recebimento_disponivel === true &&
        checkoutContext.etapa_checkout_habilitada === false
      ) {
        return "";
      }
      if (
        this.selected &&
        this.paymentPending &&
        checkoutContext.habilitado !== true &&
        checkoutContext.motivo_bloqueio
      ) {
        return checkoutContext.motivo_bloqueio;
      }

      if (
        !this.selected ||
        !this.paymentPending ||
        this.hasCashierPermission ||
        (!this.selected?.checkout_context?.atendimento_id &&
          !this.checkoutActionOfferedByServer)
      ) {
        return "";
      }

      return this.selected.tipo_entrega === "delivery"
        ? "A entrega foi realizada e aguarda retorno. Peça a um operador com permissão de Caixa e Entrega para receber e concluir o retorno."
        : "Este pedido aguarda recebimento. Peça a um operador com permissão de Caixa para receber e concluir a retirada.";
    },
    paymentAttentionMessage() {
      return this.paymentPending
        ? "O recebimento precisa ser confirmado antes da conclusão."
        : "Pagamento confirmado.";
    },
    availableActions() {
      if (!this.selected) return [];
      const actions = this.selected.available_actions;
      // Array vazio é uma decisão explícita de autorização do backend. O
      // fallback por status existe apenas para respostas legadas que não
      // enviavam o campo; nunca deve recriar uma ação que a API removeu.
      if (Array.isArray(actions)) {
        const normalized = Array.from(
          new Set(
            actions
              .filter(
                (action) =>
                  typeof action !== "object" || action.enabled !== false
              )
              .map((action) => this.normalizeActionKey(action))
              .filter(Boolean)
          )
        );
        let allowed = normalized.filter((action) => {
          if (
            action === "refuse" &&
            ["pago", "aprovado"].includes(
              this.selected.pagamento_status_raw || this.payment.raw_status
            )
          ) {
            return false;
          }
          if (
            ["mark_ready", "finalize_delivery"].includes(action) &&
            !this.hasCashierPermission
          ) {
            return false;
          }
          if (
            ["checkout_pickup", "checkout_delivery_return"].includes(action) &&
            !this.canOpenCheckoutSelected
          ) {
            return false;
          }
          if (
            !this.hasCashierPermission &&
            ["checkout_pickup", "checkout_delivery_return", "cancel"].includes(
              action
            )
          ) {
            return false;
          }
          if (
            !this.hasDeliveryPermission &&
            ["assign_courier", "complete_delivery_return"].includes(action)
          ) {
            return false;
          }
          if (
            this.paymentPending &&
            ["complete_pickup", "complete_delivery_return"].includes(action)
          ) {
            return false;
          }
          if (
            !this.paymentPending &&
            ["checkout_pickup", "checkout_delivery_return"].includes(action)
          ) {
            return false;
          }
          return true;
        });

        if (this.deliveryAwaitingReturn) {
          allowed = allowed.filter((action) => action !== "assign_courier");
          const returnAction = this.paymentPending
            ? this.canOpenCheckoutSelected
              ? "checkout_delivery_return"
              : null
            : this.hasDeliveryPermission
            ? "complete_delivery_return"
            : null;
          if (returnAction) allowed.unshift(returnAction);
        }

        return Array.from(new Set(allowed));
      }
      return this.fallbackAvailableActions(this.selected);
    },
    deliveryPrimaryAction() {
      if (
        !this.selected ||
        !this.hasDeliveryPermission ||
        TERMINAL_STATUSES.includes(this.selected.status) ||
        !(
          this.selected.lane === "entrega" ||
          ["pronto", "saiu_para_entrega", "entregue"].includes(
            this.selected.status
          )
        ) ||
        this.deliveryAwaitingReturn ||
        !this.availableActions.includes("assign_courier")
      ) {
        return null;
      }
      return {
        ...PRIMARY_ACTIONS.assign_courier,
        label:
          this.selected?.entrega?.entregador || this.selected?.delivery?.courier
            ? "Trocar entregador"
            : "Atribuir entregador",
      };
    },
    primaryAction() {
      if (!this.selected) return null;
      if (this.deliveryAwaitingReturn) {
        const returnKey = this.paymentPending
          ? "checkout_delivery_return"
          : "complete_delivery_return";
        return this.availableActions.includes(returnKey)
          ? PRIMARY_ACTIONS[returnKey]
          : null;
      }
      if (
        this.selected.tipo_entrega === "delivery" &&
        this.deliveryPrimaryAction
      ) {
        return this.deliveryPrimaryAction;
      }
      const serverPrimary = this.selected.primary_action;
      if (
        serverPrimary &&
        typeof serverPrimary === "object" &&
        serverPrimary.enabled === false
      ) {
        return null;
      }
      const serverKey = this.normalizeActionKey(
        serverPrimary && typeof serverPrimary === "object"
          ? serverPrimary.key || serverPrimary.code || serverPrimary.action
          : serverPrimary
      );
      if (serverKey === "checkout_pickup" && !this.hasCashierPermission) {
        return null;
      }
      if (
        serverKey &&
        PRIMARY_ACTIONS[serverKey] &&
        this.availableActions.includes(serverKey)
      ) {
        return {
          ...PRIMARY_ACTIONS[serverKey],
          ...(serverPrimary && typeof serverPrimary === "object"
            ? serverPrimary
            : {}),
          key: serverKey,
        };
      }
      const priority = [
        "checkout_delivery_return",
        "complete_delivery_return",
        "checkout_pickup",
        "accept",
        "production",
        "complete_pickup",
        "open_production",
      ];
      const key = priority.find((action) =>
        this.availableActions.includes(action)
      );
      return key ? PRIMARY_ACTIONS[key] : null;
    },
    lastRefreshLabel() {
      if (!this.lastRefreshAt) return "Ainda não atualizado";
      return `Atualizado às ${this.lastRefreshAt.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}`;
    },
  },
  watch: {
    "$route.query.pedido"(value) {
      if (!value && this.drawerVisible) this.closeDrawer(false);
      if (value && String(value) !== String(this.selectedId)) {
        this.openOrderById(value);
      }
    },
  },
  async mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    const requestedView =
      this.$route.query.view || this.$route.query.return_view || "operation";
    if (["operation", "stale", "history"].includes(requestedView)) {
      this.activeView = requestedView;
    }
    const requestedLane =
      this.$route.query.lane || this.$route.query.return_lane;
    const requestedOrder =
      this.$route.query.pedido || this.$route.query.return_pedido;
    if (BOARD_LANES.some((lane) => lane.key === requestedLane)) {
      this.activeMobileLane = requestedLane;
    }
    if (this.deliveryOnlyProfile) this.activeMobileLane = "entrega";
    this.replaceQuery({
      view: this.activeView,
      lane: this.activeMobileLane,
      return_to: undefined,
      return_view: undefined,
      return_lane: undefined,
      return_pedido: undefined,
    });

    await this.loadCurrentView(true);
    if (requestedOrder) await this.openOrderById(requestedOrder);

    this.orderSound = createOrderSound();
    document.addEventListener("pointerdown", this.unlockOrderSound);
    document.addEventListener("keydown", this.unlockOrderSound);
    this.monitorNewOrders();

    this.refreshInterval = window.setInterval(
      this.pollCurrentView,
      REFRESH_INTERVAL_MS
    );
    this.clockInterval = window.setInterval(() => {
      this.clockNow = Date.now();
    }, CLOCK_INTERVAL_MS);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.orderMonitorStopped = true;
    document.removeEventListener("pointerdown", this.unlockOrderSound);
    document.removeEventListener("keydown", this.unlockOrderSound);
    if (this.orderSound) this.orderSound.close();
    window.clearInterval(this.refreshInterval);
    window.clearInterval(this.clockInterval);
    window.clearTimeout(this.searchTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    async unlockOrderSound(event) {
      if (
        event &&
        event.target &&
        event.target.closest &&
        event.target.closest(".pedidos-sound-button")
      )
        return;
      if (!this.soundEnabled || this.soundReady || !this.orderSound) return;
      this.soundReady = await this.orderSound.unlock();
      if (this.orderMonitorStopped) return;
      if (this.soundReady && this.pendingOrderSound) {
        this.pendingOrderSound = false;
        this.orderSound.play();
      }
    },
    async toggleOrderSound() {
      if (this.soundEnabled && this.soundReady) {
        this.soundEnabled = false;
        this.pendingOrderSound = false;
        if (this.orderSound) this.orderSound.stop();
      } else {
        this.soundEnabled = true;
        await this.unlockOrderSound();
        if (this.soundReady) this.orderSound.play();
        else
          this.showError(
            "O navegador não liberou o áudio. Verifique o som e clique em Ativar som novamente."
          );
      }
      saveSoundPreference(this.soundEnabled);
    },
    async monitorNewOrders() {
      if (
        this.deliveryOnlyProfile ||
        this.monitoringOrders ||
        this.orderMonitorStopped
      )
        return;
      this.monitoringOrders = true;
      try {
        const { data } = await this.$http.get(
          "/restaurante/pedidos-online/novos/monitor",
          {
            params:
              this.newOrderCursor === null
                ? {}
                : { after_id: this.newOrderCursor },
          }
        );
        if (this.orderMonitorStopped) return;
        const snapshot = unwrapResponse(data);
        if (
          !Array.isArray(snapshot.new_ids) ||
          !Number.isSafeInteger(snapshot.last_id)
        )
          return;
        const initialized = this.newOrderCursor !== null;
        this.newOrderCursor = snapshot.last_id;
        if (initialized && snapshot.new_ids.length) {
          const message =
            snapshot.new_ids.length === 1
              ? "Novo pedido digital recebido!"
              : `${snapshot.new_ids.length} novos pedidos digitais recebidos!`;
          this.liveMessage = message;
          this.$toast.success(message);
          if (this.soundEnabled) {
            let played = false;
            if (this.orderSound) {
              played = this.orderSound.play();
            }
            if (!played) {
              this.soundReady = false;
              this.pendingOrderSound = true;
            }
          }
        }
        return true;
      } catch (error) {
        if (!this.orderMonitorStopped)
          this.showError(
            this.apiErrorMessage(
              error,
              "Falha ao verificar novos pedidos. Nova tentativa em 60 segundos."
            )
          );
      } finally {
        this.monitoringOrders = false;
      }
    },
    refreshCurrentView() {
      return this.loadCurrentView(true);
    },
    loadCurrentView(showLoading = true) {
      return this.activeView === "history"
        ? this.loadHistory(true, showLoading)
        : this.loadBoard(this.currentScope, showLoading);
    },
    async loadBoard(scope = "active", showLoading = true) {
      if (this.draggedOrder) return;
      const sequence = ++this.boardRequestSequence;
      if (showLoading) this.loadingBoard = true;
      this.clearFeedback();
      try {
        let normalized;
        if (!this.legacyBoardEndpoint) {
          try {
            const { data } = await this.$http.get(
              "/restaurante/pedidos-online/quadro",
              {
                params: {
                  ...this.boardApiFilters(),
                  scope,
                  per_lane: 20,
                },
              }
            );
            normalized = normalizeBoard(data);
          } catch (error) {
            if (!isMissingEndpointError(error)) throw error;
            this.legacyBoardEndpoint = true;
          }
        }
        if (!normalized) normalized = await this.loadLegacyBoard(scope);
        if (sequence !== this.boardRequestSequence || this.draggedOrder) return;
        this.$set(this.boards, scope, normalized);
        this.lastRefreshAt = new Date();
        const total = BOARD_LANES.reduce(
          (sum, lane) => sum + normalized.lanes[lane.key].total,
          0
        );
        this.liveMessage = `${total} pedidos carregados no quadro.`;
        return true;
      } catch (error) {
        if (sequence !== this.boardRequestSequence) return;
        this.showError(
          this.apiErrorMessage(
            error,
            "Não foi possível carregar o quadro de pedidos."
          )
        );
      } finally {
        if (sequence === this.boardRequestSequence && showLoading) {
          this.loadingBoard = false;
        }
      }
    },
    async loadLegacyBoard(scope) {
      const { data } = await this.$http.get("/restaurante/pedidos-online", {
        params: {
          ...this.boardApiFilters(),
          per_page: 150,
          page: 1,
        },
      });
      const board = boardFromLegacyList(data, scope);
      const now = board.server_time
        ? new Date(board.server_time).getTime()
        : Date.now();
      BOARD_LANES.forEach((lane) => {
        const source = board.lanes[lane.key];
        source.items = source.items.filter((order) => {
          const dateValue =
            order.finalized_at ||
            order.status_changed_at ||
            order.stage_entered_at ||
            order.updated_at ||
            order.created_at;
          const timestamp = dateValue ? new Date(dateValue).getTime() : now;
          const olderThan24h =
            Number.isFinite(timestamp) && now - timestamp >= 86400000;
          const terminal = TERMINAL_STATUSES.includes(order.status);
          if (lane.terminal) return scope === "active" && !olderThan24h;
          return scope === "stale" ? olderThan24h && !terminal : !olderThan24h;
        });
        source.total = source.items.length;
      });
      if (scope === "active") {
        board.stale_total = BOARD_LANES.reduce((total, lane) => {
          if (lane.terminal) return total;
          return (
            total +
            (
              boardFromLegacyList(data, "stale").lanes[lane.key].items || []
            ).filter((order) => {
              const value =
                order.status_changed_at ||
                order.stage_entered_at ||
                order.created_at;
              const time = value ? new Date(value).getTime() : now;
              return Number.isFinite(time) && now - time >= 86400000;
            }).length
          );
        }, 0);
      }
      return board;
    },
    async loadMoreLane(laneKey) {
      const lane = this.currentBoard.lanes[laneKey];
      if (!lane?.next_cursor || this.loadingMoreLane) return;
      this.loadingMoreLane = laneKey;
      try {
        const { data } = await this.$http.get(
          "/restaurante/pedidos-online/quadro",
          {
            params: {
              ...this.boardApiFilters(),
              scope: this.currentScope,
              lane: laneKey,
              cursor: lane.next_cursor,
              [`cursor_${laneKey}`]: lane.next_cursor,
              per_lane: 20,
            },
          }
        );
        const incoming = normalizeBoard(data);
        const updated = {
          ...this.currentBoard,
          ...incoming,
          lanes: {
            ...this.currentBoard.lanes,
            [laneKey]: mergeLane(lane, incoming.lanes[laneKey], true),
          },
        };
        this.$set(this.boards, this.currentScope, updated);
      } catch (error) {
        this.showError(
          this.apiErrorMessage(error, "Não foi possível carregar mais pedidos.")
        );
      } finally {
        this.loadingMoreLane = null;
      }
    },
    async loadHistory(reset = true, showLoading = true) {
      const sequence = ++this.historyRequestSequence;
      if (showLoading) this.loadingHistory = true;
      this.clearFeedback();
      try {
        let response;
        let legacyHistory = false;
        try {
          response = await this.$http.get(
            "/restaurante/pedidos-online/historico",
            {
              params: {
                ...this.historyApiFilters(),
                cursor: reset ? undefined : this.history.next_cursor,
                per_page: 30,
              },
            }
          );
        } catch (error) {
          if (!isMissingEndpointError(error)) throw error;
          legacyHistory = true;
          response = await this.$http.get("/restaurante/pedidos-online", {
            params: {
              ...this.boardApiFilters(),
              aba: "historico",
              page: reset ? 1 : 2,
              per_page: 100,
            },
          });
        }
        if (sequence !== this.historyRequestSequence) return;
        const incoming = normalizeHistory(response.data);
        if (legacyHistory) {
          incoming.items = this.filterLegacyHistory(incoming.items);
          incoming.total = incoming.items.length;
          incoming.next_cursor = null;
        }
        this.history = reset
          ? incoming
          : {
              ...incoming,
              items: this.mergeOrders(this.history.items, incoming.items),
            };
        this.lastRefreshAt = new Date();
        this.liveMessage = `${this.history.total} pedidos encontrados no histórico.`;
      } catch (error) {
        if (sequence !== this.historyRequestSequence) return;
        this.showError(
          this.apiErrorMessage(error, "Não foi possível carregar o histórico.")
        );
      } finally {
        if (sequence === this.historyRequestSequence && showLoading) {
          this.loadingHistory = false;
        }
      }
    },
    async pollCurrentView() {
      // O monitor continua no Historico, durante acoes e com a aba em segundo plano.
      const monitoramento = this.monitorNewOrders();
      if (
        document.hidden ||
        this.draggedOrder ||
        this.actionLoading ||
        this.loadingCurrentView ||
        this.activeView === "history"
      ) {
        if (await monitoramento) await this.imprimirAposAceite();
        return;
      }
      const quadroCarregado = await this.loadBoard(this.currentScope, false);
      if (this.drawerVisible && !this.detailLoading)
        await this.refreshSelectedPedido(false);
      const monitorado = await monitoramento;
      if (monitorado || quadroCarregado) await this.imprimirAposAceite();
    },
    onVisibilityChange() {
      if (!document.hidden) this.monitorNewOrders();
      if (!document.hidden && this.activeView !== "history") {
        this.loadBoard(this.currentScope, false);
      }
    },
    async selectView(view) {
      if (view === this.activeView) return;
      this.activeView = view;
      this.filterPanelOpen = false;
      this.closeDrawer(false);
      this.replaceQuery({
        view,
        lane: this.activeMobileLane,
        pedido: undefined,
      });
      await this.loadCurrentView(true);
    },
    selectMobileLane(lane) {
      this.activeMobileLane = lane;
      this.replaceQuery({ view: this.activeView, lane });
    },
    scheduleSearch() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => {
        this.appliedFilters.busca = this.draftFilters.busca.trim();
        this.loadCurrentView(true);
      }, SEARCH_DEBOUNCE_MS);
    },
    applyFilters() {
      window.clearTimeout(this.searchTimer);
      this.appliedFilters = {
        ...this.draftFilters,
        busca: this.draftFilters.busca.trim(),
      };
      if (this.activeView === "history") this.historyPreset = "custom";
      this.filterPanelOpen = false;
      this.loadCurrentView(true);
    },
    clearFilters() {
      const period = historyPeriod(7);
      this.draftFilters = {
        busca: "",
        tipo_entrega: null,
        pagamento_status: null,
        status: null,
        ...period,
      };
      this.appliedFilters = { ...this.draftFilters };
      this.historyPreset = "7d";
      this.filterPanelOpen = false;
      this.loadCurrentView(true);
    },
    applyHistoryPreset(key) {
      this.historyPreset = key;
      if (key === "custom") {
        this.filterPanelOpen = true;
        return;
      }
      const days = key === "today" ? 1 : key === "30d" ? 30 : 7;
      const period = historyPeriod(days);
      Object.assign(this.draftFilters, period);
      Object.assign(this.appliedFilters, period);
      this.loadHistory(true, true);
    },
    boardApiFilters() {
      return {
        busca: this.appliedFilters.busca || undefined,
        tipo_entrega: this.appliedFilters.tipo_entrega || undefined,
        pagamento_status: this.appliedFilters.pagamento_status || undefined,
      };
    },
    historyApiFilters() {
      return {
        ...this.boardApiFilters(),
        from: this.appliedFilters.from || undefined,
        to: this.appliedFilters.to || undefined,
        "status[]": this.appliedFilters.status || undefined,
      };
    },
    async openOrder(order, element = null) {
      if (element) this.triggerElement = element;
      this.selected = normalizeCard(order, order.lane);
      this.drawerVisible = true;
      this.timeline = { items: [], next_cursor: null, loading: false };
      this.replaceQuery({
        view: this.activeView,
        lane: order.lane || this.activeMobileLane,
        pedido: orderId(order),
        return_to: undefined,
        return_view: undefined,
        return_lane: undefined,
        return_pedido: undefined,
      });
      return this.refreshSelectedPedido(true);
    },
    async openOrderById(id) {
      const local = this.findOrder(id);
      if (local) {
        await this.openOrder(local);
        return;
      }
      this.selected = { id, codigo: `#${id}`, available_actions: [] };
      this.drawerVisible = true;
      this.replaceQuery({
        view: this.activeView,
        lane: this.activeMobileLane,
        pedido: id,
        return_to: undefined,
        return_view: undefined,
        return_lane: undefined,
        return_pedido: undefined,
      });
      await this.refreshSelectedPedido(true);
    },
    async refreshSelectedPedido(showLoading = true) {
      if (!this.selected) return;
      const id = orderId(this.selected);
      if (!id) return;
      const sequence = ++this.detailRequestSequence;
      const selectedKey = String(id);
      if (showLoading) this.detailLoading = true;
      try {
        const { data } = await this.$http.get(
          `/restaurante/pedidos-online/${id}`
        );
        if (
          sequence !== this.detailRequestSequence ||
          !this.selected ||
          String(orderId(this.selected)) !== selectedKey
        ) {
          return;
        }
        const detail = unwrapResponse(data);
        this.selected = normalizeCard(detail.pedido || detail, detail.lane);
        if (this.selected.tipo_entrega === "delivery") {
          this.loadCouriers();
        }
        const legacyTimeline =
          this.selected.historicos || this.selected.historico || [];
        if (Array.isArray(legacyTimeline) && legacyTimeline.length) {
          this.timeline.items = legacyTimeline;
        }
        return true;
      } catch (error) {
        if (sequence === this.detailRequestSequence) {
          const status = Number(error?.response?.status || 0);
          const message = this.apiErrorMessage(error, "");
          if (
            status === 404 ||
            ([400, 422].includes(status) &&
              /pedido online n[aã]o encontrado/i.test(message))
          ) {
            this.detailLoading = false;
            this.closeDrawer(false);
            this.replaceQuery({ pedido: undefined, return_pedido: undefined });
            this.liveMessage =
              "O pedido do link não está mais disponível. O quadro foi mantido atualizado.";
            return;
          }
          this.showError(
            this.apiErrorMessage(error, "Não foi possível carregar o pedido.")
          );
        }
      } finally {
        if (sequence === this.detailRequestSequence) this.detailLoading = false;
      }
    },
    closeDrawer(updateQuery = true) {
      this.drawerVisible = false;
      this.detailRequestSequence += 1;
      this.timelineRequestSequence += 1;
      this.selected = null;
      this.timeline = { items: [], next_cursor: null, loading: false };
      if (updateQuery) {
        this.replaceQuery({
          pedido: undefined,
          return_pedido: undefined,
          return_to: undefined,
        });
      }
      this.$nextTick(() => {
        if (this.triggerElement && document.contains(this.triggerElement)) {
          this.triggerElement.focus();
        } else {
          document
            .querySelector(
              `.pedido-kanban-lane.is-mobile-active .pedido-kanban-card, .pedido-kanban-lane .pedido-kanban-card`
            )
            ?.focus();
        }
        this.triggerElement = null;
      });
    },
    async loadTimeline(append = false) {
      if (!this.selected || this.timeline.loading) return;
      const id = orderId(this.selected);
      const sequence = ++this.timelineRequestSequence;
      this.timeline.loading = true;
      try {
        const { data } = await this.$http.get(
          `/restaurante/pedidos-online/${id}/timeline`,
          {
            params: {
              cursor: append ? this.timeline.next_cursor : undefined,
              per_page: 20,
            },
          }
        );
        if (sequence !== this.timelineRequestSequence) return;
        const value = unwrapResponse(data);
        const items = value.items || value.historicos || [];
        this.timeline.items = append
          ? this.mergeTimeline(this.timeline.items, items)
          : items;
        this.timeline.next_cursor = value.next_cursor || null;
      } catch (error) {
        if (!isMissingEndpointError(error)) {
          this.showError(
            this.apiErrorMessage(
              error,
              "Não foi possível carregar a linha do tempo."
            )
          );
        }
      } finally {
        if (sequence === this.timelineRequestSequence)
          this.timeline.loading = false;
      }
    },
    normalizeActionKey(action) {
      if (!action) return null;
      const value =
        typeof action === "object"
          ? action.key || action.code || action.action
          : action;
      return ACTION_ALIASES[value] || value || null;
    },
    moveAction(order, laneKey) {
      if (
        !order ||
        order.lane === laneKey ||
        TERMINAL_STATUSES.includes(order.status)
      )
        return null;
      const actions = Array.isArray(order.available_actions)
        ? order.available_actions
            .filter(
              (action) => typeof action !== "object" || action.enabled !== false
            )
            .map(this.normalizeActionKey)
        : [];
      const offered = (action) => actions.includes(action);
      if (laneKey === "producao" && order.lane === "novos" && offered("accept"))
        return "accept";
      const readyLane =
        order.tipo_entrega === "delivery" ? "entrega" : "retirada";
      if (
        laneKey === readyLane &&
        order.status === "em_preparo" &&
        this.hasCashierPermission &&
        offered("mark_ready")
      )
        return "mark_ready";
      if (laneKey !== "finalizados") return null;
      if (order.tipo_entrega === "delivery") {
        if (this.hasCashierPermission && offered("finalize_delivery"))
          return "finalize_delivery";
        const deliveryPermission =
          order.capabilities?.dispatch === true ||
          (userAccessStore.access?.restauranteEntregasOnline &&
            !userAccessStore.disabled?.restauranteEntregasOnline);
        if (!deliveryPermission) return null;
        if (offered("complete_delivery_return"))
          return "complete_delivery_return";
        if (this.hasCashierPermission && offered("checkout_delivery_return"))
          return "checkout_delivery_return";
      } else {
        if (offered("complete_pickup")) return "complete_pickup";
        if (this.hasCashierPermission && offered("checkout_pickup"))
          return "checkout_pickup";
      }
      return null;
    },
    async moveOrder(order, laneKey) {
      if (this.actionLoading || this.detailLoading || this.drawerVisible)
        return;
      const action = this.moveAction(order, laneKey);
      if (!action) return;
      const refreshed = await this.openOrder(order);
      if (!refreshed) return;
      if (
        !this.selected ||
        String(orderId(this.selected)) !== String(orderId(order))
      )
        return;
      if (
        this.moveAction(this.selected, laneKey) !== action ||
        !this.availableActions.includes(action)
      ) {
        this.showError(
          "Este pedido mudou ou não permite esta movimentação. Revise os dados atualizados."
        );
        return;
      }
      const handlers = {
        accept: () => this.aceitarPedido(),
        mark_ready: () => this.markReady(),
        finalize_delivery: () => this.finalizeDelivery(),
        complete_pickup: () => this.completePickup(),
        checkout_pickup: () => this.openCharge("concluir_retirada"),
        complete_delivery_return: () => this.completeDeliveryReturn(),
        checkout_delivery_return: () =>
          this.openCharge("concluir_retorno_entrega"),
      };
      await handlers[action]();
    },
    fallbackAvailableActions(order) {
      const status = order.status;
      const type = order.tipo_entrega;
      if (["novo", "recebido", "aguardando_aceite"].includes(status)) {
        return ["accept", "refuse", "print", "timeline"].filter(
          (action) =>
            action !== "refuse" ||
            !["pago", "aprovado"].includes(
              order.pagamento_status_raw || order.payment?.raw_status
            )
        );
      }
      if (status === "aceito") {
        return ["production", "cancel", "print", "timeline"];
      }
      if (status === "em_preparo") {
        return ["open_production", "cancel", "print", "timeline"];
      }
      if (type === "pickup" && ["pronto", "entregue"].includes(status)) {
        if (this.paymentPending && !this.canOpenCheckoutSelected) {
          return ["print", "timeline"];
        }
        return [
          this.paymentPending && this.canOpenCheckoutSelected
            ? "checkout_pickup"
            : "complete_pickup",
          "print",
          "timeline",
        ];
      }
      if (
        type === "delivery" &&
        ["pronto", "saiu_para_entrega", "entregue"].includes(status)
      ) {
        if (isDeliveryAwaitingReturn(order)) {
          if (this.paymentPending && this.canOpenCheckoutSelected) {
            return ["checkout_delivery_return", "print", "timeline"];
          }
          if (!this.paymentPending && this.hasDeliveryPermission) {
            return ["complete_delivery_return", "print", "timeline"];
          }
          return ["print", "timeline"];
        }
        return this.hasDeliveryPermission
          ? ["assign_courier", "print", "timeline"]
          : ["print", "timeline"];
      }
      return ["print", "timeline"];
    },
    runPrimaryAction(courierId) {
      const action = this.primaryAction?.key;
      const handlers = {
        accept: () => this.aceitarPedido(),
        production: () => this.alterarStatus("em_preparo"),
        open_production: () => this.openProduction(),
        checkout_pickup: () => this.openCharge("concluir_retirada"),
        complete_pickup: () => this.completePickup(),
        assign_courier: () => this.assignCourier(courierId),
        checkout_delivery_return: () =>
          this.openCharge("concluir_retorno_entrega"),
        complete_delivery_return: () => this.completeDeliveryReturn(),
        finalize_delivery: () => this.finalizeDelivery(),
      };
      if (handlers[action]) handlers[action]();
    },
    runSecondaryAction(action) {
      const handlers = {
        refuse: () => this.recusarPedido(),
        cancel: () => this.cancelarPedido(),
        open_production: () => this.openProduction(),
        mark_ready: () => this.markReady(),
        finalize_delivery: () => this.finalizeDelivery(),
      };
      if (handlers[action]) handlers[action]();
    },
    aceitarPedido() {
      return this.executarAcao(
        "aceitar",
        "Pedido aceito e enviado para produção."
      );
    },
    async recusarPedido() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Recusar este pedido?",
        input: "text",
        inputPlaceholder: "Justificativa da recusa (opcional)",
        inputAttributes: {
          "aria-label": "Justificativa da recusa",
          maxlength: "255",
        },
        customClass: { container: "pedido-online-dialog-container" },
        focusConfirm: false,
        onOpen: () => this.$swal.getInput()?.focus(),
        showCancelButton: true,
        confirmButtonText: "Recusar pedido",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });
      if (!result.isConfirmed) return;
      await this.executarAcao("recusar", "Pedido recusado.", {
        motivo: result.value || null,
      });
    },
    async cancelarPedido() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar este pedido?",
        text: "O motivo ficará registrado na linha do tempo.",
        input: "text",
        inputPlaceholder: "Informe o motivo do cancelamento",
        customClass: { container: "pedido-online-dialog-container" },
        focusConfirm: false,
        onOpen: () => this.$swal.getInput()?.focus(),
        inputValidator: (value) =>
          value?.trim() ? undefined : "Informe o motivo do cancelamento.",
        showCancelButton: true,
        confirmButtonText: "Cancelar pedido",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });
      if (!result.isConfirmed) return;
      await this.alterarStatus("cancelado", result.value);
    },
    alterarStatus(status, motivo = null) {
      return this.executarAcao("status", "Status atualizado.", {
        status,
        motivo,
      });
    },
    completePickup() {
      return this.executarAcao("retirada/concluir", "Retirada concluída.", {});
    },
    markReady() {
      return this.executarAcao("producao/concluir", "Pedido pronto.");
    },
    async finalizeDelivery() {
      if (
        !this.selected ||
        this.actionLoading ||
        !this.availableActions.includes("finalize_delivery")
      )
        return;
      const pending = this.paymentPending;
      const id = orderId(this.selected);
      const idempotencyKey = uuidLike();
      this.actionLoading = true;
      this.clearFeedback();
      try {
        const { data } = await this.$http.post(
          `/restaurante/pedidos-online/${id}/entrega/finalizar`,
          {
            status: pending ? "entregue" : "concluido",
            expected_version: this.selected.version,
            idempotency_key: idempotencyKey,
          },
          { headers: { "Idempotency-Key": idempotencyKey } }
        );
        const detail = unwrapResponse(data);
        this.selected = normalizeCard(detail.pedido || detail, detail.lane);
        if (pending) {
          this.liveMessage =
            "Entrega confirmada. Confirme o recebimento para finalizar o pedido.";
          await this.loadBoard(this.currentScope, false);
          this.openCharge("concluir_retorno_entrega");
        } else {
          this.$toast.success("Pedido finalizado pelo painel.");
          this.liveMessage = "Pedido finalizado pelo painel.";
          this.closeDrawer();
          await this.loadBoard(this.currentScope, false);
        }
      } catch (error) {
        const current =
          error?.response?.data?.data?.current ||
          error?.response?.data?.current;
        if (current) this.selected = normalizeCard(current, current.lane);
        else if (error?.response?.status === 409)
          await this.refreshSelectedPedido(false);
        this.showError(
          error?.response?.status === 409
            ? "Este pedido foi atualizado em outra tela. Revise os dados e tente novamente."
            : this.apiErrorMessage(
                error,
                "Não foi possível finalizar o pedido."
              )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    completeDeliveryReturn() {
      return this.executeDeliveryReturn(
        "Retorno concluído e pedido finalizado."
      );
    },
    async executarAcao(action, successMessage, payload = {}) {
      if (!this.selected || this.actionLoading) return;
      const id = orderId(this.selected);
      if (!id) return;
      const idempotencyKey = uuidLike();
      this.actionLoading = true;
      this.clearFeedback();
      try {
        const { data } = await this.$http.post(
          `/restaurante/pedidos-online/${id}/${action}`,
          {
            ...payload,
            expected_version: this.selected.version ?? undefined,
            idempotency_key: idempotencyKey,
          },
          { headers: { "Idempotency-Key": idempotencyKey } }
        );
        if (action === "recusar") {
          const pedidoAtualizado = unwrapResponse(data);
          if (pedidoAtualizado.status !== "recusado") {
            throw new Error(
              "A recusa não foi confirmada. Atualize o pedido e tente novamente."
            );
          }
          this.aplicarRecusaAoQuadro(id, pedidoAtualizado);
        }
        this.$toast.success(successMessage);
        this.liveMessage = successMessage;
        this.closeDrawer();
        if (action === "aceitar") {
          this.imprimirAposAceite(true);
        }
        await this.loadBoard(this.currentScope, false);
      } catch (error) {
        const current =
          error?.response?.data?.data?.current ||
          error?.response?.data?.current ||
          null;
        if (current) this.selected = normalizeCard(current, current.lane);
        const conflict = error?.response?.status === 409;
        if (conflict && !current) {
          await this.refreshSelectedPedido(false);
        }
        this.showError(
          conflict
            ? "Este pedido foi atualizado em outra tela. Revise os dados e tente novamente."
            : this.apiErrorMessage(
                error,
                "Não foi possível concluir a ação do pedido."
              )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    aplicarRecusaAoQuadro(id, pedidoAtualizado) {
      ["active", "stale"].forEach((scope) => {
        const board = this.boards[scope];
        const novos = board?.lanes?.novos;
        if (!novos) return;
        const items = novos.items.filter(
          (order) => String(orderId(order)) !== String(id)
        );
        if (items.length === novos.items.length) return;

        const lanes = {
          ...board.lanes,
          novos: {
            ...novos,
            items,
            total: Math.max(
              0,
              novos.total - (novos.items.length - items.length)
            ),
          },
        };
        if (scope === "active" && lanes.finalizados) {
          lanes.finalizados = {
            ...lanes.finalizados,
            items: [
              normalizeCard(pedidoAtualizado, "finalizados"),
              ...lanes.finalizados.items,
            ].slice(0, 20),
            total: lanes.finalizados.total + 1,
          };
        }
        this.$set(this.boards, scope, { ...board, lanes });
      });
    },
    async imprimirAposAceite(repetirSeOcupado = false) {
      try {
        await processarFilaImpressao(this.$http, {
          repetirSeOcupado,
          onError: (error) => {
            if (error?.response?.data?.msg || error?.response?.data?.message) {
              return;
            }
            this.$toast.warning(
              error.message ||
                "Existem tickets pendentes de impressão no Restaurante."
            );
          },
        });
      } catch (error) {
        if (error?.response?.data?.msg || error?.response?.data?.message)
          return;
        this.$toast.warning(
          "Não foi possível consultar as impressões pendentes do Restaurante."
        );
      }
    },
    openCharge(operation) {
      if (!this.selected) {
        this.showError(
          "O checkout ainda não está disponível para este pedido."
        );
        return;
      }
      if (!this.hasCashierPermission) {
        this.showError(
          "Somente um operador com permissão de Caixa pode receber este pedido."
        );
        return;
      }
      const context = this.checkoutContext;
      const pedidoOnlineId = orderId(this.selected);
      if (
        this.selected.tipo_entrega === "delivery" &&
        !this.deliveryAwaitingReturn
      ) {
        this.showError(
          "Marque a entrega como realizada antes de abrir o recebimento do retorno."
        );
        return;
      }
      if (
        this.selected.tipo_entrega === "delivery" &&
        !this.hasDeliveryPermission
      ) {
        this.showError(
          "O operador precisa das permissões de Caixa e Entrega para receber e concluir o retorno."
        );
        return;
      }
      if (!context?.atendimento_id || !pedidoOnlineId) {
        this.showError(
          "O atendimento fiscal deste pedido ainda não está disponível. Atualize o pedido e tente novamente."
        );
        return;
      }

      this.$router.push({
        name: "restaurantePedidoOnlineCheckout",
        params: {
          pedidoOnlineId,
          atendimentoId: context.atendimento_id,
        },
        query: {
          operacao_final: operation || "concluir_retirada",
          comanda_id: context.comanda_id,
          modo_comandas: "1",
          return_to: "pedidos_online",
          return_view: this.activeView,
          return_lane: this.selected.lane || this.activeMobileLane,
          return_pedido: pedidoOnlineId,
          expected_version: this.selected.version,
        },
      });
    },
    async executeDeliveryReturn(successMessage) {
      if (!this.selected || this.actionLoading) return;
      const id = orderId(this.selected);
      if (!id) return;
      const idempotencyKey = uuidLike();
      this.actionLoading = true;
      this.clearFeedback();
      try {
        await this.$http.post(
          `/restaurante/entregas-online/pedidos/${id}/retornar`,
          {
            expected_version: this.selected.version,
            idempotency_key: idempotencyKey,
            observacao: "Retorno concluído pelo Kanban de Pedidos Online.",
          },
          { headers: { "Idempotency-Key": idempotencyKey } }
        );
        this.$toast.success(successMessage);
        this.liveMessage = successMessage;
        this.closeDrawer();
        await this.loadBoard(this.currentScope, false);
      } catch (error) {
        const current =
          error?.response?.data?.data?.current ||
          error?.response?.data?.current ||
          null;
        if (current) this.selected = normalizeCard(current, current.lane);
        if (error?.response?.status === 409 && !current) {
          await this.refreshSelectedPedido(false);
        }
        this.showError(
          error?.response?.status === 409
            ? "Este pedido foi atualizado em outra tela. Revise o retorno e tente novamente."
            : this.apiErrorMessage(
                error,
                "Não foi possível concluir o retorno da entrega."
              )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    async loadCouriers() {
      if (!this.hasDeliveryPermission || this.couriersLoading) return;
      this.couriersLoading = true;
      try {
        const { data } = await this.$http.get(
          "/restaurante/entregas-online/entregadores"
        );
        const value = unwrapResponse(data);
        this.couriers = Array.isArray(value)
          ? value.filter((courier) => courier.ativo !== false)
          : [];
      } catch (error) {
        this.showError(
          this.apiErrorMessage(
            error,
            "Não foi possível carregar os entregadores ativos."
          )
        );
      } finally {
        this.couriersLoading = false;
      }
    },
    async assignCourier(courierId) {
      if (!this.selected || this.actionLoading) return;
      if (!courierId) {
        this.showError("Selecione o entregador responsável.");
        return;
      }
      const id = orderId(this.selected);
      const idempotencyKey = uuidLike();
      this.actionLoading = true;
      this.clearFeedback();
      try {
        await this.$http.post(
          "/restaurante/entregas-online/atribuir",
          {
            pedido_online_id: id,
            entregador_id: Number(courierId),
            expected_version: this.selected.version,
            idempotency_key: idempotencyKey,
          },
          { headers: { "Idempotency-Key": idempotencyKey } }
        );
        this.$toast.success("Entregador atribuído ao pedido.");
        this.liveMessage = "Entregador atribuído ao pedido.";
        await this.refreshSelectedPedido(false);
        await this.loadBoard(this.currentScope, false);
      } catch (error) {
        const current =
          error?.response?.data?.data?.current ||
          error?.response?.data?.current ||
          null;
        if (current) this.selected = normalizeCard(current, current.lane);
        if (error?.response?.status === 409 && !current) {
          await this.refreshSelectedPedido(false);
        }
        this.showError(
          error?.response?.status === 409
            ? "Este pedido foi atualizado em outra tela. Revise o entregador e tente novamente."
            : this.apiErrorMessage(
                error,
                "Não foi possível atribuir o entregador."
              )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    openProduction() {
      this.$router.push({
        name: "restauranteProducao",
        query: this.selected
          ? { pedido_online_id: orderId(this.selected) }
          : undefined,
      });
    },
    async imprimirPedidoCompleto() {
      if (!this.selected || this.actionLoading) return;
      const id = orderId(this.selected);
      let popup = null;
      try {
        this.actionLoading = true;
        popup = window.open("", "_blank");
        if (popup) popup.opener = null;
        const response = await this.$http.get(
          `/restaurante/pedidos-online/${id}/impressao`,
          { responseType: "blob" }
        );
        const contentType =
          response.headers?.["content-type"] || "text/html;charset=UTF-8";
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        if (popup) popup.location.href = url;
        else window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        if (popup && !popup.closed) popup.close();
        this.showError(
          this.apiErrorMessage(error, "Não foi possível abrir a impressão.")
        );
      } finally {
        this.actionLoading = false;
      }
    },
    findOrder(id) {
      const wanted = String(id);
      const boardOrders = BOARD_LANES.flatMap(
        (lane) => this.currentBoard.lanes[lane.key].items
      );
      return [...boardOrders, ...this.history.items].find(
        (order) => String(orderId(order)) === wanted
      );
    },
    mergeOrders(current, incoming) {
      const result = new Map();
      [...current, ...incoming].forEach((order) =>
        result.set(String(orderId(order)), order)
      );
      return Array.from(result.values());
    },
    mergeTimeline(current, incoming) {
      const result = new Map();
      [...current, ...incoming].forEach((event, index) =>
        result.set(String(event.id || `${event.created_at}-${index}`), event)
      );
      return Array.from(result.values());
    },
    filterLegacyHistory(items) {
      const from = this.appliedFilters.from
        ? new Date(`${this.appliedFilters.from}T00:00:00`).getTime()
        : Number.NEGATIVE_INFINITY;
      const to = this.appliedFilters.to
        ? new Date(`${this.appliedFilters.to}T23:59:59.999`).getTime()
        : Number.POSITIVE_INFINITY;
      return items.filter((order) => {
        if (!TERMINAL_STATUSES.includes(order.status)) return false;
        if (
          this.appliedFilters.status &&
          order.status !== this.appliedFilters.status
        ) {
          return false;
        }
        if (
          this.appliedFilters.tipo_entrega &&
          order.tipo_entrega !== this.appliedFilters.tipo_entrega
        ) {
          return false;
        }
        const paymentStatus =
          order.payment?.status || order.pagamento?.status || null;
        if (
          this.appliedFilters.pagamento_status &&
          paymentStatus !== this.appliedFilters.pagamento_status
        ) {
          return false;
        }
        const value =
          order.finalized_at ||
          order.concluido_em ||
          order.cancelado_em ||
          order.recusado_em ||
          order.updated_at;
        const timestamp = value ? new Date(value).getTime() : NaN;
        return (
          Number.isFinite(timestamp) && timestamp >= from && timestamp <= to
        );
      });
    },
    pedidoKey: orderId,
    replaceQuery(changes) {
      const query = { ...this.$route.query, ...changes };
      Object.keys(query).forEach((key) => {
        if (
          query[key] === undefined ||
          query[key] === null ||
          query[key] === ""
        ) {
          delete query[key];
        }
      });
      this.$router.replace({ query }).catch(() => {});
    },
    clearFeedback() {
      this.feedbackMessage = "";
    },
    showError(message) {
      this.feedbackMessage = message;
      this.feedbackType = "error";
      this.liveMessage = message;
      const now = Date.now();
      if (
        message !== this.lastErrorToastMessage ||
        now - this.lastErrorToastAt >= 60000
      ) {
        this.lastErrorToastMessage = message;
        this.lastErrorToastAt = now;
        this.$toast.error(message);
      }
    },
    apiErrorMessage(error, fallback) {
      const data = error?.response?.data;
      if (typeof data === "string") return data;
      return data?.msg || data?.message || data?.error || fallback;
    },
  },
};
</script>

<style scoped>
.restaurante-pedidos-online {
  height: calc(100vh - 100px);
  height: calc(100dvh - 100px);
  margin: -25px;
  max-height: calc(100vh - 100px);
  max-height: calc(100dvh - 100px);
  min-height: 580px;
  min-width: 0;
}

.pedidos-shell {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  height: 100%;
  margin: 0;
  min-height: 0;
  overflow: hidden;
}

.pedidos-page-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  padding: 8px 10px 10px;
}

.pedidos-commandbar {
  align-items: center;
  border-bottom: 1px solid #e4e6ef;
  display: grid;
  flex: 0 0 auto;
  gap: 10px;
  grid-template-columns: auto minmax(420px, 1fr);
  margin-bottom: 8px;
  min-width: 0;
}

.pedidos-view-tabs {
  display: flex;
  gap: 6px;
  margin: 0;
  overflow-x: auto;
}

.pedidos-view-tabs button {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: #7e8299;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 7px;
  min-height: 44px;
  padding: 0 11px;
}

.pedidos-view-tabs button.active {
  border-bottom-color: #3699ff;
  color: #2875bf;
}

.pedidos-view-tabs button:focus-visible,
.kanban-stage-tabs button:focus-visible,
.history-presets button:focus-visible {
  outline: 2px solid #3699ff;
  outline-offset: -2px;
}

.view-count,
.filter-count {
  align-items: center;
  background: #fff1f2;
  border-radius: 999px;
  color: #c93649;
  display: inline-flex;
  font-size: 10px;
  justify-content: center;
  min-height: 21px;
  min-width: 21px;
  padding: 0 6px;
}

.pedidos-toolbar {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin: 0;
  min-width: 0;
}

.pedidos-search {
  align-items: center;
  background: #fff;
  border: 1px solid #d1d3e0;
  border-radius: 8px;
  display: flex;
  margin: 0;
  max-width: 420px;
  min-height: 44px;
  padding: 0 13px;
  width: 100%;
}

.pedidos-search:focus-within {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.12);
}

.pedidos-search i {
  color: #7e8299;
}

.pedidos-search input {
  background: transparent;
  border: 0;
  color: #3f4254;
  flex: 1;
  min-width: 0;
  outline: none;
  padding: 10px 9px;
}

.pedidos-filter-wrapper {
  flex: 0 0 auto;
  position: relative;
}

.filter-toggle,
.pedidos-refresh-button {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 44px;
}

.pedidos-refresh-button {
  flex: 0 0 44px;
  padding: 0;
  width: 44px;
}

.pedidos-sound-button {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
  min-height: 44px;
  white-space: nowrap;
}

.pedidos-auto-refresh {
  color: #7e8299;
  font-size: 11px;
  white-space: nowrap;
}

.pedidos-filter-panel {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(24, 28, 50, 0.16);
  display: grid;
  gap: 13px;
  padding: 16px;
  position: absolute;
  right: 0;
  top: calc(100% + 7px);
  width: 350px;
  z-index: 20;
}

.filter-panel-heading,
.filter-panel-actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.filter-panel-heading strong {
  color: #181c32;
}

.filter-panel-heading button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #7e8299;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.pedidos-filter-panel label {
  margin: 0;
}

.pedidos-filter-panel label > span {
  color: #5e6278;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 5px;
}

.pedidos-filter-panel .form-control,
.filter-panel-actions .btn {
  min-height: 44px;
}

.history-period-fields {
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.filter-panel-actions {
  border-top: 1px solid #ebedf3;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 13px;
}

.history-presets {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: -4px 0 14px;
}

.history-presets > span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
}

.history-presets button {
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 999px;
  color: #5e6278;
  font-size: 11px;
  font-weight: 800;
  min-height: 44px;
  padding: 0 12px;
}

.history-presets button.active {
  background: #eef6ff;
  border-color: #a8d3ff;
  color: #2875bf;
}

.pedidos-feedback {
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 14px;
  padding: 11px 13px;
}

.pedidos-feedback.is-error {
  background: #fff1f2;
  border: 1px solid #ffc4ca;
  color: #a92e3d;
}

.stale-explanation {
  align-items: flex-start;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 9px;
  color: #a05a00;
  display: flex;
  gap: 11px;
  margin-bottom: 13px;
  padding: 12px 14px;
}

.stale-explanation div,
.stale-explanation span {
  display: block;
}

.stale-explanation span {
  color: #7e5b27;
  font-size: 11px;
  margin-top: 2px;
}

.kanban-stage-tabs {
  display: none;
}

.kanban-board {
  display: flex;
  flex: 1 1 0;
  gap: 11px;
  min-height: 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  padding: 1px 1px 8px;
  scrollbar-gutter: stable;
}

.history-view {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.history-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.history-heading h2 {
  color: #181c32;
  font-size: 17px;
  font-weight: 800;
  margin: 0;
}

.history-heading p {
  color: #7e8299;
  font-size: 11px;
  margin: 3px 0 0;
}

.history-heading > strong {
  color: #5e6278;
  font-size: 12px;
}

.history-grid {
  display: grid;
  gap: 11px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.history-load-more {
  display: block;
  margin: 18px auto 0;
  min-height: 44px;
}

.page-state {
  align-items: center;
  border: 1px dashed #d1d3e0;
  border-radius: 10px;
  color: #7e8299;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  gap: 10px;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

.page-empty i {
  color: #b5b5c3;
  font-size: 28px;
}

.page-empty strong {
  color: #5e6278;
}

.page-empty span {
  font-size: 11px;
}

@media (max-width: 1450px) {
  .history-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1199px) {
  .pedidos-commandbar {
    align-items: stretch;
    grid-template-columns: minmax(0, 1fr);
  }

  .pedidos-toolbar {
    justify-content: stretch;
  }

  .pedidos-search {
    max-width: none;
  }

  .kanban-stage-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 11px;
    overflow-x: auto;
    padding-bottom: 3px;
  }

  .kanban-stage-tabs button {
    align-items: center;
    background: #f3f6f9;
    border: 1px solid #e4e6ef;
    border-radius: 8px;
    color: #5e6278;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 800;
    gap: 7px;
    min-height: 44px;
    padding: 0 13px;
  }

  .kanban-stage-tabs button.active {
    background: #eef6ff;
    border-color: #3699ff;
    color: #2875bf;
  }

  .kanban-stage-tabs button span {
    align-items: center;
    background: #fff;
    border-radius: 999px;
    display: inline-flex;
    justify-content: center;
    min-height: 21px;
    min-width: 21px;
    padding: 0 5px;
  }

  .kanban-board {
    flex: 1 1 0;
    overflow-x: visible;
  }
  .kanban-board.is-dragging {
    overflow-x: auto;
  }

  .history-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .restaurante-pedidos-online {
    height: calc(100vh - 85px);
    height: calc(100dvh - 85px);
    margin: -15px;
    max-height: calc(100vh - 85px);
    max-height: calc(100dvh - 85px);
    min-height: 520px;
  }

  .pedidos-page-body {
    padding: 6px 8px 8px;
  }

  .pedidos-view-tabs button {
    padding: 0 10px;
  }

  .pedidos-toolbar {
    align-items: stretch;
    flex-wrap: wrap;
  }
  .pedidos-auto-refresh {
    align-self: center;
  }

  .pedidos-filter-panel {
    bottom: 12px;
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    position: fixed;
    right: 12px;
    top: 12px;
    width: calc(100vw - 24px);
    z-index: 100;
  }

  .filter-toggle {
    font-size: 0;
    justify-content: center;
    width: 48px;
  }

  .filter-toggle i {
    font-size: 13px;
    margin: 0 !important;
  }

  .filter-count {
    font-size: 9px;
    margin-left: 3px;
  }

  .history-grid {
    grid-template-columns: 1fr;
  }

  .history-heading {
    align-items: flex-start;
  }
}

@media (max-width: 420px) {
  .history-period-fields {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.swal2-container.pedido-online-dialog-container {
  z-index: 1200;
}
</style>
